import React, { useState, useEffect, useContext } from 'react';
import { string, bool, shape } from 'prop-types';
import { ExperienceContext } from '@thd-nucleus/experience-context';
import { useLayoutGroupProps } from '@thd-olt-component-react/layout-manager';
import { Carousel } from '@thd-olt-component-react/carousel';
import { useHelmet, LinkTag } from '@thd-nucleus/thd-helmet';

import classNames from 'classnames/bind';
import { makeApiRequest, shouldMakeApiRequest } from '../../utils/requestHelpers';
import {
  getSchema,
  getSkus,
  getDefaultBanner,
  triggerNewRelic,
  parseIfJson
} from '../../utils/helpers';

import styles from './SponsoredBanner.module.scss';
import { newRelicConstants } from '../../constants';

let cx = classNames.bind(styles);

const hoistedImgFunction = ({ data }) => {
  const { isBrowse, avoidPreload, defaultBannerInfo } = data;
  if (isBrowse && !avoidPreload) {
    return [
      new LinkTag({ rel: 'preload', as: 'image', href: defaultBannerInfo.imgSrc, id: 'spBannerPreloadLink' })
    ];
  }
  return null;
};

const SponsoredBanner = (props) => {
  const [lastRequestScenario, setLastRequestScenario] = useState({});
  const [hasData, setHasData] = useState(false);
  let { renderNewBanner = false } = useLayoutGroupProps({ props });
  const experienceContext = useContext(ExperienceContext);
  const { pageContext, avoidPreload } = props;
  let { schema } = props;
  const { utilizeRvData } = props;

  const defaultBannerInfo = getDefaultBanner(experienceContext);
  schema = getSchema({ schema, pageContext });
  const schemaId = schema + '__banner';

  if (schema === 'localad_sponsored' || schema === 'hp_sponsored_auth' || schema === 'hp_sponsored') {
    renderNewBanner = true;
  }

  const data = {
    avoidPreload,
    channel: experienceContext.channel,
    isBrowse: pageContext.isBrowse,
    defaultBannerInfo
  };

  const onRevjetMessage = (evt) => {
    try {
      const messageData = parseIfJson(evt.data);
      if (messageData.module === 'REVJET_Creative' && messageData.method === 'dispatchEvent'
        && messageData.properties?.eventObject?.type === 'click') {
        triggerNewRelic(newRelicConstants.STANDARD_BANNER, newRelicConstants.CLICK);
      }
    } catch (event) {
      throw new Error('Failed to send click to New Relic');
    }
  };

  useHelmet('preloadBanner', { data }, hoistedImgFunction);

  useEffect(() => {
    if (renderNewBanner) {
      // For browse, search, category and event page schemas
      if (pageContext?.label === 'browse-search') {
        const isValidScenario = shouldMakeApiRequest({ lastRequestScenario, experienceContext, pageContext });

        if (isValidScenario) {
          setLastRequestScenario({
            url: window.location.pathname + window.location.search,
            skus: getSkus(pageContext)
          });

          makeApiRequest({
            schema,
            schemaId,
            experienceContext,
            pageContext,
            setHasData,
            utilizeRvData
          });
        }
      } else {
        //  All other schemas
        makeApiRequest({
          schema,
          schemaId,
          experienceContext,
          pageContext,
          setHasData,
          utilizeRvData
        });
      }
    }
  }, [pageContext.data]);

  useEffect(() => {
    window.addEventListener('message', onRevjetMessage, false);

    return () => { window.removeEventListener('message', onRevjetMessage); };
  }, []);

  if (!renderNewBanner) {
    return null;
  }

  return (
    <div className={cx('sponsored_banner_container')} style={defaultBannerInfo.bgCss}>
      {!hasData && (
        <div id="default_banner_carousel" style={defaultBannerInfo.spacingCss}>
          <a href={defaultBannerInfo.href}>
            { /* eslint-disable-next-line */}
            <img width="100%" src={defaultBannerInfo.imgSrc} style={{ position: 'absolute', top: 0, left: 0 }} />
          </a>
        </div>
      )}

      { hasData && (
        <Carousel multiItem={false} totalItems={2}>
          <div id="default_banner_carousel" style={defaultBannerInfo.spacingCss}>
            <a href={defaultBannerInfo.href}>
              { /* eslint-disable-next-line */}
              <img width="100%" src={defaultBannerInfo.imgSrc} style={{ position: 'absolute', top: 0, left: 0 }} />
            </a>
          </div>
          <div id={schemaId} className="nucleus-banner" data-usenewimpressionlogic="true" />
        </Carousel>
      )}
    </div>
  );
};

SponsoredBanner.propTypes = {
  schema: string,
  pageContext: shape({
    label: string,
    data: shape(),
    keyword: string,
    isSearch: bool,
    isCategory: bool,
    isBrowse: bool
  }),
  avoidPreload: bool,
  utilizeRvData: bool
};

SponsoredBanner.defaultProps = {
  schema: '',
  pageContext: {},
  avoidPreload: true,
  utilizeRvData: false,
};

export { SponsoredBanner };
